/**
 * Solution 2: Naming Conventions
 *
 * Standardized color naming rules to ensure consistency across the codebase.
 * All colors should follow the naming convention: category_subcategory_variant
 */

/**
 * Color naming convention rules
 */
export const NAMING_CONVENTIONS = {
	// Format: {category}_{subcategory}_{variant}
	// Examples:
	// - primary_green (primary color, green variant)
	// - secondary_blue (secondary color, blue variant)
	// - brand_pet_primary (brand color, pet tower, primary)
	// - ui_success (UI color, success state)
	// - ui_error (UI color, error state)

	CATEGORIES: {
		PRIMARY: 'primary',
		SECONDARY: 'secondary',
		BRAND: 'brand',
		UI: 'ui',
		STATE: 'state',
		NEUTRAL: 'neutral',
	},

	SUBCATEGORIES: {
		// Brand subcategories
		PET: 'pet',
		VET: 'vet',
		FARM: 'farm',
		CORPORATE: 'corporate',

		// UI subcategories
		SUCCESS: 'success',
		ERROR: 'error',
		WARNING: 'warning',
		INFO: 'info',
		DISABLED: 'disabled',
		HOVER: 'hover',
		ACTIVE: 'active',
		FOCUS: 'focus',

		// Neutral subcategories
		LIGHT: 'light',
		DARK: 'dark',
		GRAY: 'gray',
	},

	VARIANTS: {
		LIGHT: 'light',
		DARK: 'dark',
		MEDIUM: 'medium',
		PALE: 'pale',
		VIBRANT: 'vibrant',
	},

	RULES: [
		'Always use lowercase',
		'Use snake_case (no spaces, no camelCase)',
		'Be descriptive (not "color1", "color2")',
		'Include tower if tower-specific',
		'Include variant if multiple shades',
		'Format: {category}_{subcategory}_{variant}',
	],
}

/**
 * Validate color name against naming conventions
 */
export function isValidColorName(name: string): boolean {
	// Check if lowercase
	if (name !== name.toLowerCase()) {
		return false
	}

	// Check if snake_case (only lowercase letters, numbers, and underscores)
	if (!/^[a-z0-9_]+$/.test(name)) {
		return false
	}

	// Check if not empty
	if (name.length === 0) {
		return false
	}

	// Check if doesn't start or end with underscore
	if (name.startsWith('_') || name.endsWith('_')) {
		return false
	}

	// Check if doesn't have consecutive underscores
	if (name.includes('__')) {
		return false
	}

	return true
}

/**
 * Get validation error message for invalid color name
 */
export function getColorNameValidationError(name: string): string | null {
	if (name !== name.toLowerCase()) {
		return 'Color name must be lowercase'
	}

	if (!/^[a-z0-9_]+$/.test(name)) {
		return 'Color name must use snake_case (only lowercase letters, numbers, and underscores)'
	}

	if (name.length === 0) {
		return 'Color name cannot be empty'
	}

	if (name.startsWith('_') || name.endsWith('_')) {
		return 'Color name cannot start or end with underscore'
	}

	if (name.includes('__')) {
		return 'Color name cannot have consecutive underscores'
	}

	return null
}

/**
 * Suggest a valid color name based on input
 */
export function suggestColorName(input: string): string {
	return input
		.toLowerCase()
		.replace(/[ \t]+/g, '_') // Replace spaces with underscores
		.replace(/-/g, '_') // Replace hyphens with underscores
		.replace(/[^a-z0-9_]/g, '') // Remove invalid characters
		.split('_')
		.filter(Boolean)
		.join('_') // Collapse consecutive underscores and trim leading/trailing
}

/**
 * Parse color name into components
 */
export function parseColorName(name: string): {
	category?: string
	subcategory?: string
	variant?: string
	isValid: boolean
} {
	if (!isValidColorName(name)) {
		return {isValid: false}
	}

	const parts = name.split('_')

	return {
		category: parts[0],
		subcategory: parts[1],
		variant: parts[2],
		isValid: true,
	}
}

/**
 * Generate color name from components
 */
export function generateColorName(
	category: string,
	subcategory?: string,
	variant?: string
): string {
	const parts = [category]

	if (subcategory) {
		parts.push(subcategory)
	}

	if (variant) {
		parts.push(variant)
	}

	return parts.join('_').toLowerCase()
}

/**
 * Validate hex code format
 */
export function isValidHexCode(hex: string): boolean {
	// Remove # if present
	const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex

	// Check if valid hex code (3 or 6 characters)
	return /^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(cleanHex)
}

/**
 * Normalize hex code (lowercase, with #)
 */
export function normalizeHexCode(hex: string): string {
	const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex
	return `#${cleanHex.toLowerCase()}`
}

/**
 * Get hex code without # prefix
 */
export function getHexCodeWithoutHash(hex: string): string {
	return hex.startsWith('#') ? hex.slice(1) : hex
}
